import Vec2 from 'vec2';

import { DEFAULT_DEPTH } from '~/core/constants';

/**
 * Creates a function for drawing straight lines (horizontal or vertical only).
 *
 * @param points - Array of Vec2 points in the drawing
 * @returns A function that creates new points based on cursor position and reference point
 */
export const makeGetNewPoint =
  (points: Array<Vec2>) =>
  /**
   * @param referencePointIndex - Index of the reference point
   * @param cursorPosition - Current cursor position
   * @param minimumDistance - Minimum distance needed before creating a new point
   * @returns Either a new point or null if no new point should be created
   */
  (
    referencePointIndex: number,
    cursorPosition: Vec2,
    minimumDistance: number = DEFAULT_DEPTH
  ): Vec2 | null => {
    const referencePoint = points[referencePointIndex];

    if (!referencePoint) {
      return null;
    }

    // Calculate the distance moved in each direction
    const deltaX = Math.abs(referencePoint.x - cursorPosition.x);
    const deltaY = Math.abs(referencePoint.y - cursorPosition.y);

    // Handle the first two points differently (initial direction setting)
    const isInitialDirection =
      points.length < 2 || (points.length === 2 && referencePointIndex === 0);

    if (isInitialDirection) {
      const isHorizontalMovement = deltaX > deltaY;

      return isHorizontalMovement
        ? new Vec2(cursorPosition.x, referencePoint.y) // Move horizontally
        : new Vec2(referencePoint.x, cursorPosition.y); // Move vertically
    }

    // For subsequent points, only create new point if minimum distance is exceeded
    if (deltaX >= minimumDistance) {
      // Create horizontal line by keeping Y constant
      return new Vec2(cursorPosition.x, referencePoint.y);
    }

    if (deltaY >= minimumDistance) {
      // Create vertical line by keeping X constant
      return new Vec2(referencePoint.x, cursorPosition.y);
    }

    // No new point needed if cursor haven't moved far enough
    return null;
  };
